<template>
    <div class="page__content bookings">
        <h2 class="page__headline">
            {{ $t('bookings.bookings.headline') }}:
        </h2>
        <div class="columns">
            <div class="column is-8">
                <p class="page__copy">
                    {{ $t('bookings.bookings.copy') }}
                </p>
            </div>
            <div class="column is-4">
                <div class="is-clearfix text-right">
                    <router-link exact :to="{ name: 'new-booking', params: {lang: lang} }" class="btn button is-primary"  v-if="hasContract" @click.native="addBooking()">{{ $t('bookings.addBooking') }}</router-link>
                    <a
                        v-if="hasContract"
                        :disabled="!isPublished"
                        class="button is-primary tooltip-activator"
                        target="_blank"
                        :href="checkoutUrl"
                    >
                        <i class="fa fa-external-link fa-lg" aria-hidden="true"></i>
                        <div class="tooltip tooltip--is-bottom-left">{{ $t('bookings.testBooking') }}</div>
                    </a>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <div class="tabs is-boxed">
                    <ul>
                        <li :class="{ 'is-active': tab === 3 }">
                            <router-link exact :to="{ name: 'bookings', query: this.updateQuery({tab: 3, page: 1}), params: {lang: lang} }" @click.native="tabChanged(3)">
                                <span class="icon is-small"><i class="fa fa-inbox"></i></span>
                                <span>{{ $t('bookings.bookings.tabs.all') }}</span>
                            </router-link>
                        </li>
                        <li :class="{ 'is-active': tab === 0 }">
                            <router-link exact :to="{ name: 'bookings', query: this.updateQuery({tab: 0, page: 1}), params: {lang: lang} }" @click.native="tabChanged(0)">
                                <span class="icon is-small"><i class="fa fa-star"></i></span>
                                <span>{{ $t('bookings.bookings.tabs.recent') }}</span>
                            </router-link>
                        </li>
                        <li :class="{ 'is-active': tab === 1 }">
                            <router-link exact :to="{ name: 'bookings', query: this.updateQuery({tab: 1, page: 1}), params: {lang: lang} }" @click.native="tabChanged(1)">
                                <span class="icon is-small"><i class="fa fa-archive"></i></span>
                                <span>{{ $t('bookings.bookings.tabs.old') }}</span>
                            </router-link>
                        </li>
                        <li :class="{ 'is-active': tab === 2 }">
                            <router-link exact :to="{ name: 'bookings', query: this.updateQuery({tab: 2, page: 1}), params: {lang: lang} }" @click.native="tabChanged(2)">
                                <span class="icon is-small"><i class="fa fa-ban"></i></span>
                                <span>{{ $t('bookings.bookings.tabs.canceled') }}</span>
                            </router-link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="columns is-multiline">
            <div class="column is-12">
                <booking-filter v-model="filterData" :pmsApiActive="pmsApiActive" @input="onFilterChanged"/>
            </div>
            <div class="column is-12">
                <table class="table is-striped is-bordered booking__table" :class="{'is-loading': isLoading}">
                    <sortable-table-head v-model="tableHeader" @input="sortingChanged()"></sortable-table-head>
                    <tbody>
                        <tr v-for="(booking, index) in bookings"
                            :key="booking.booking_id"
                            :class="getAllBookingClass(booking)">
                            <td v-if="pmsApiActive">
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.confirmed_by_pms_at | localizedDate('L, LTS') }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.created_at | localizedDate('L, LTS') }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.booking_number }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.date_from | localizedDate('L') }} bis {{ booking.date_until | localizedDate('L') }} ({{getNumberOfNights(booking)}})
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.first_name }} {{ booking.last_name }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ getCampsiteTypes(booking) }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.num_persons }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.price | currency(booking.currency, lang) }}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ getPaidAmount(booking) }}
                                </router-link>

                            </td>
                            <td>
                                <router-link
                                    v-if="booking.portal || booking.affiliate "
                                    exact
                                    :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }"
                                >
                                    {{ booking.portal || booking.affiliate.name}}
                                </router-link>
                            </td>
                            <td>
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    {{ booking.portal_booking_id }}
                                </router-link>
                            </td>
                            <td v-if="showStatusColumn" class="booking__status-cell">
                                <router-link exact :to="{ name: 'booking-detail', params: { bookingId: booking.booking_id, lang: lang } }">
                                    <span v-if="booking.booking_status === 'internally_canceled'">{{ $t('bookings.bookings.status.internallyCanceled') }}</span>
                                    <span v-else-if="booking.booking_status === 'canceled'">{{ $t('bookings.bookings.status.canceled') }}</span>
                                    <span v-else-if="booking.booking_status === 'confirmed'">{{ $t('bookings.bookings.status.confirmed') }}</span>
                                    <br v-if="booking.cancelation_reason">
                                    <small v-if="booking.cancelation_reason">{{ $t('bookings.bookings.table.reasonLabel') }}: {{ getCancelationReasonText(booking) }}</small>
                                </router-link>
                            </td>
                            <td>
                                <span v-if="!isBookingCanceled(booking)" class="icon-checkbox" title="Cancel booking" @click="selectRow(index, booking)">
                                        <i class="fa fa-trash icon-cancel"></i>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <pagination :loading="isLoading" :params="this.$route.query" :currentPage="Number(this.page)" :limit="limit" :numPages="numPages" />
            </div>
        </div>

        <div class="modal is-active no-print" v-if="showCancelModal">
            <div class="modal-background" @click="closeCancelModal()"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ $t('bookingDetails.bookings.cancelTitle') }}</p>
                    <button class="delete" @click="closeCancelModal()"></button>
                </header><!-- force_cancel -->
                <section class="modal-card-body">
                    <textarea class="textarea" v-model="cancelReason" :placeholder="$t('bookingDetails.bookings.reasonPlaceholder')"></textarea>
                </section>
                <footer class="modal-card-foot is-clearfix">
                    <a class="button" @click="closeCancelModal()">{{ $t('generic.cancel') }}</a>
                    <a class="button is-danger" :disabled="!cancelReason.trim()"  @click="cancelBooking(selectedBooking)">
                        <span class="icon" >
                            <i class="fa fa-trash" aria-hidden="true"></i>
                        </span>
                        <span>{{ $t('bookingDetails.bookings.cancel') }}</span>
                    </a>
                </footer>
            </div>
        </div>


    </div>


</template>

<script>
    import moment from 'moment';
    import {Api} from '../../../api';
    import {AppModel} from '../../../models';
    import {EventBus, Tracking} from '../../../utils';
    import {BookingForm, BookingFilter, Pagination, IconCheckbox, SortableTableHead} from '../../components';
    import filters from '../../../filters';

    const _filters = [
        {booking_status: 'confirmed', is_young: true},
        {booking_status: 'confirmed', is_old: true},
        {booking_status: 'canceled'},
        {booking_status: 'all'},
    ];

    export default {
        name: 'bookings',
        components: {
            BookingForm,
            BookingFilter,
            Pagination,
            IconCheckbox,
            SortableTableHead,
        },

        filters: filters,

        data() {
            return {
                showCancelModal: false,
                selectedBooking: null,
                bookings: [],
                numPages: 1,
                limit: 10,
                isLoading: false,
                hasContract: AppModel.hasContract,
                isPublished: AppModel.isPublished,
                pmsApiActive: false,
                filterData: {
                    is_new: this.$route.query.is_new || '',
                    is_processed: this.$route.query.is_processed || '',
                    date_from: this.$route.query.date_from,
                    date_until: this.$route.query.date_until,
                    q: this.$route.query.q,
                },
                tableHeader: [],
                cancelReason: '',
                selectedRow: null,
            };
        },

        computed: {
            checkoutUrl() {
                const checkoutUrl = process.env.CHECKOUT_URL || 'http://localhost:8080';

                return checkoutUrl + '/de/' + AppModel.activeCampground.slug;
            },
            filters() {
                return _filters;
            },
            computedFilterData() {
                return {
                    is_new: this.$route.query.is_new || '',
                    is_processed: this.$route.query.is_processed || '',
                    date_from_min: this.$route.query.date_from,
                    date_from_max: this.$route.query.date_until,
                    date_until_min: this.$route.query.date_from,
                    date_until_max: this.$route.query.date_until,
                    q: this.$route.query.q,
                };
            },
            tab() {
                return Number(this.$route.query.tab) || 0;
            },
            showStatusColumn() {
                // Upcoming/Past are always 'confirmed', so the Status column would be
                // the same for every row there - only show it on Canceled (2) and All (3),
                // where it's actually informative.
                return this.tab === 2 || this.tab === 3;
            },
            page() {
                return Number(this.$route.query.page) || 1;
            },
            lang() {
                return AppModel.lang;
            },
        },

        created() {
            EventBus.$on(EventBus.FETCH_BOOKINGS_START, this.onFetchBookingsStart);
            EventBus.$on(EventBus.FETCH_BOOKINGS_DONE, this.onFetchBookingsDone);
            EventBus.$on(EventBus.BOOTSTRAP_DONE, this.onBootstrapDone);
            EventBus.$on(EventBus.CAMPGROUND_CHANGED, this.onBootstrapDone);
            this.onBootstrapDone();
        },

        watch: {
            $route(newValue, oldValue) {
                if(newValue.query !== oldValue.query) {
                    this.fetchBookings();
                }
            },
        },

        methods: {
            selectRow(index, booking) {
                this.selectedRow = index; // Speichere den aktuellen Index
                console.log('Aktuelle Zeile:', index, this.bookings[index]);
                this.openCancelModal(booking);
            },
            openCancelModal(booking) {
                this.cancelReason = '';
                this.selectedBooking = booking;
                this.showCancelModal = true;
            },
            closeCancelModal() {
                this.showCancelModal = false;
                this.selectedBooking = null;
            },
            cancelBooking(booking) {
                console.log('cancelBooking', booking, this.cancelReason);
                const data = {
                    booking_status: 'canceled',
                    cancelation_reason: this.cancelReason,
                    notes: null,
                };
                Api.bookings.updateBookingDetail(booking.booking_id, data)
                    .then((response) => {
                        if(response.ok) {
                            this.booking.notes = this.notes;
                            this.booking.booking_status = response.body.booking_status;
                            this.booking.cancelation_reason = response.body.cancelation_reason;
                            this.isEditMode = false;
                            this.onUpdateDone();
                        } else {
                            this.onUpdateFailed(response);
                        }
                    }, () => {
                        this.onUpdateFailed();
                    });

                booking.booking_status = 'canceled';
                const rows = document.querySelectorAll('table tbody tr');
                const index = this.selectedRow;
                if(rows[index]) {
                    rows[index].classList.add('is-canceled');
                    const lastCell = rows[index].querySelector('td:last-child');
                    if(lastCell) {
                        lastCell.textContent = ''; // oder lastCell.innerHTML = '';
                    }
                }
                this.closeCancelModal();
            },

            fetchBookings() {
                this.hasContract = AppModel.hasContract;
                let query = Object.assign({}, this.$route.query);
                let filter = Object.assign(this.getFilters(), this.computedFilterData);
                filter = Object.assign(filter, query);
                Api.bookings.fetchBookings(filter);
            },

            getFilters() {
                return Object.assign(
                    {limit: this.limit, offset: (this.page - 1) * this.limit},
                    this.filters[this.tab]
                );
            },

            isBookingCanceled(booking) {
                if(this.tab === 3 && booking && (booking.booking_status === 'canceled' || booking.booking_status === 'internally_canceled')) {
                    return true;
                }
                return false;
            },

            getCancelationReasonText(booking) {
                const path = `bookings.bookings.cancelReasons.${booking.cancelation_reason}`;
                if(!this.$te(path)) {
                    // free text a human or portal wrote - nothing to translate
                    return booking.cancelation_reason;
                }
                if(booking.cancelation_reason === 'canceled_by_portal') {
                    return this.$t(path, {name: booking.portal || ''});
                }
                return this.$t(path);
            },

            onBootstrapDone() {
                if(AppModel.activeCampground) {
                    this.hasContract = AppModel.hasContract;
                    this.isPublished = AppModel.isPublished;
                    this.fetchBookings();
                }
            },

            onFetchBookingsStart() {
                this.isLoading = true;
            },

            onFetchBookingsDone(data) {
                this.isLoading = false;
                if(data.success) {
                    this.bookings = data.body.results;
                    this.numPages = Math.ceil(data.body.count / this.limit);
                    this.pmsApiActive = !!AppModel.activeCampground.settings.pms_api_active;

                    let th = [];
                    if(this.pmsApiActive) {
                        th = [{sorting: this.getDefaultSorting('confirmed_by_pms_at'), label: this.$t('bookings.filter.isPMSconfirmed'), classes: ['is-narrow']}];
                    }

                    let statusTh = [];
                    if(this.showStatusColumn) {
                        statusTh = [
                            {label: this.$t('bookings.bookings.table.status')},
                        ];
                    }

                    this.tableHeader = [
                        ...th,
                        {sorting: this.getDefaultSorting('created_at'), label: this.$t('bookings.bookings.table.dateCreated')},
                        {sorting: this.getDefaultSorting('_booking_number'), label: this.$t('bookings.bookings.table.bookingNumber')},
                        {sorting: this.getDefaultSorting('date_from'), label: this.$t('bookings.bookings.table.period')},
                        {sorting: this.getDefaultSorting('first_name'), label: this.$t('bookings.bookings.table.name')},
                        {label: this.$t('bookings.bookings.table.campsiteType')},
                        {label: this.$t('bookings.bookings.table.guests')},
                        {sorting: this.getDefaultSorting('price'), label: this.$t('bookings.bookings.table.amount')},
                        {label: this.$t('bookings.bookings.table.paid')},
                        {label: this.$t('bookings.bookings.table.portal')},
                        {label: this.$t('bookings.bookings.table.portal_id')},
                        ...statusTh,
                        {label: 'C'},
                    ];
                }
            },

            onFilterChanged() {
                let filter = Object.assign({}, this.filterData);
                filter = Object.assign(filter, {page: 1});
                this.$router.push({name: 'bookings', query: this.updateQuery(filter)});
            },

            getCampsiteTypes(booking) {
                if(booking.acco_pms_id) {
                    return booking.acco_pms_id;
                }
                let t = [];
                for(let campsiteType of booking.campsite_types) {
                    console.log(campsiteType, booking);
                    let val = AppModel.campsiteTypes[campsiteType];
                    if(!t.includes(val)) {
                        t.push(val);
                    }
                }
                return t.join('/');
            },

            updateBookingProcess(booking, what, val) {
                alert('updateBookingProcess');
                let data = {};
                data[what] = val;
                Api.bookings.updateBookingProcess(booking.booking_id, data);
                Tracking.trackEvent('Bookings', 'updateProcess', what, val);
            },

            sortingChanged() {
                let ordering = '';
                for(let row of this.tableHeader) {
                    if(row.sorting) {
                        if(row.sorting.value !== 0) {
                            ordering = row.sorting.field;
                            if(row.sorting.value === -1) {
                                ordering = '-' + ordering;
                            }
                        }
                    }
                }
                Tracking.trackEvent('Bookings', 'sortingChanged', ordering, null);
                this.$router.push({name: 'bookings', query: this.updateQuery({ordering: ordering})});
            },

            updateQuery(update) {
                let query = Object.assign({}, this.$route.query);
                query = Object.assign(query, update);
                return query;
            },

            getDefaultSorting(fieldName) {
                let order = this.$route.query.ordering;
                if(!order) {
                    order = '-created_at';
                }
                let defaultOrder = 0;
                if(order === fieldName) {
                    defaultOrder = 1;
                } else if(order === `-${fieldName}`) {
                    defaultOrder = -1;
                }

                return {
                    field: fieldName,
                    value: defaultOrder
                };
            },

            tabChanged(tabIndex) {
                Tracking.trackEvent('Bookings', 'tabChanged', null, tabIndex);
            },

            addBooking() {
                Tracking.trackEvent('Bookings', 'addBooking', null, null);
            },

            getNumberOfNights(booking) {
                return Math.abs(moment(booking.date_until).diff(moment(booking.date_from), 'days'));
            },
            getPaidAmount(booking) {
                if(booking.paid === undefined || booking.paid === null || booking.paid === '' || booking.paid === 0) {
                    return booking.payment_system;
                }
                return this.$options.filters.currency(booking.paid, this.currency, this.lang);
            },
            getAllBookingClass(booking) {
                if(this.tab !== 3) {
                    if(booking.is_new) { return 'is-new'; }
                    return '';
                }
                if(this.isBookingCanceled(booking)) { return 'is-canceled'; }
                const today = new Date();
                const startDate = new Date(booking.date_from); // Passe das Attribut an die tatsächliche Buchungsstruktur an
                const endDate = new Date(booking.date_until);
                let newClass = booking.is_new ? 'is-new ' : '';
                if(startDate > today) {
                    newClass += 'is-future'; // Buchung liegt in der Zukunft
                } else if(endDate < today) {
                    newClass += 'is-past'; // Buchung ist in der Vergangenheit
                } else {
                    newClass += 'is-current-period'; // Buchung läuft aktuell
                }
                return newClass;
            }
        },
    };
</script>
<style lang="scss">
    @import 'bookings.scss';
</style>

<style scoped>
.icon-cancel {
    cursor: pointer;
    color: #C62828;
}
tr.is-canceled {
    background-color: #F8C9C7 !important;
}
tr.is-canceled td a {
    color: #C62828 !important;
    text-decoration: line-through;
}
tr.is-canceled td.booking__status-cell a {
    text-decoration: none;
}
tr.is-past {
    background-color: #F7F5EF;
}
tr.is-past td a {
    color: #000000 !important;
}
tr.is-future {
    background-color: #E8F6EF;
}
tr.is-future td a {
    color: #000000 !important;
}

tr.is-new td a {
    color: #000000 !important;
    font-weight: 600;
}
</style>




// WEBPACK FOOTER //
// src/views/pages/bookings/bookings.vue